import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useAuth from '../hooks/useAuth';
import { getCurrentUsuario, updateCurrentUsuario } from '../services/userService';

const UsuarioContext = createContext(undefined);

const resolveProfileEmail = (profile, fallbackEmail = null) =>
  profile?.correoElectronico ||
  profile?.email ||
  profile?.preferred_username ||
  fallbackEmail ||
  null;

export const UsuarioProvider = ({ children }) => {
  const { authFetch, profile, isAuthenticated, accountEmail } = useAuth();
  const [usuario, setUsuario] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsuario = useCallback(async () => {
    if (!isAuthenticated || !profile?.sub) {
      setUsuario(null);
      setIsLoading(false);
      setHasLoaded(false);
      setError(null);
      return null;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await getCurrentUsuario(authFetch);
      setUsuario(data ?? null);
      return data ?? null;
    } catch (err) {
      const normalizedError =
        err instanceof Error ? err : new Error('No fue posible obtener tu información.');
      setError(normalizedError);
      setUsuario(null);
      throw normalizedError;
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [authFetch, isAuthenticated, profile?.sub]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!isAuthenticated || !profile?.sub) {
        if (!active) return;
        setUsuario(null);
        setError(null);
        setHasLoaded(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const data = await getCurrentUsuario(authFetch);
        if (!active) {
          return;
        }
        setUsuario(data ?? null);
      } catch (err) {
        if (!active) {
          return;
        }
        const normalizedError =
          err instanceof Error ? err : new Error('No fue posible obtener tu información.');
        setError(normalizedError);
        setUsuario(null);
      } finally {
        if (active) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [authFetch, isAuthenticated, profile?.sub]);

  const saveUsuario = useCallback(
    async (data) => {
      const payload = { ...data };
      if (!usuario && !payload.correoElectronico) {
        const fallbackEmail = resolveProfileEmail(profile, accountEmail);
        if (fallbackEmail) {
          payload.correoElectronico = fallbackEmail;
        }
      }

      const result = await updateCurrentUsuario(authFetch, payload);
      setUsuario(result ?? null);
      setHasLoaded(true);
      return result ?? null;
    },
    [accountEmail, authFetch, profile, usuario],
  );

  const value = useMemo(
    () => ({
      usuario,
      setUsuario,
      isLoading: !hasLoaded && isLoading,
      isFetching: isLoading,
      hasLoaded,
      error,
      refreshUsuario: fetchUsuario,
      saveUsuario,
      profileEmail: resolveProfileEmail(profile, accountEmail),
    }),
    [accountEmail, error, fetchUsuario, hasLoaded, isLoading, profile, saveUsuario, usuario],
  );

  return <UsuarioContext.Provider value={value}>{children}</UsuarioContext.Provider>;
};

export const useUsuario = () => {
  const ctx = useContext(UsuarioContext);
  if (ctx === undefined) {
    throw new Error('useUsuario debe utilizarse dentro de un UsuarioProvider');
  }
  return ctx;
};

export default UsuarioContext;
